import { forwardRef, type ElementType, type MouseEventHandler } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { Typography } from '../../Typography';

/**
 * Visual shape of the button.
 *
 * - `icon` (default): vertical stack — icon on top, label underneath, with a
 *   72×48 oblong selection indicator behind the icon. Designed for the mobile
 *   bottom navigation bar.
 * - `pill`: horizontal — optional icon + label inline, the entire button is a
 *   pill shape that fills with `selectionColor` when selected. Designed for
 *   desktop top-nav links and similar horizontal-tab patterns.
 */
export type NavigationButtonVariant = 'icon' | 'pill';

type BaseNavigationButtonProps = {
  /**
   * The icon to display in the button. Required for `variant="icon"`,
   * optional for `variant="pill"` (text-only pills are common).
   */
  icon?: React.ReactNode;
  /**
   * Visual shape — see `NavigationButtonVariant` above.
   * @default 'icon'
   */
  variant?: NavigationButtonVariant;
  /**
   * Whether the button is in the selected state.
   *
   * - `variant="icon"`: shows a 72×48 oblong shape behind the icon, label
   *   below.
   * - `variant="pill"`: fills the pill background with `selectionColor`.
   * @default false
   */
  selected?: boolean;
  /**
   * The color of the icon.
   * Can be a theme color path (e.g., 'common.white') or a CSS color value.
   * @default 'common.white'
   */
  iconColor?: string;
  /**
   * The color of the label text.
   * Can be a theme color path (e.g., 'common.white') or a CSS color value.
   * @default 'common.white'
   */
  textColor?: string;
  /**
   * The background color of the button container.
   * Can be a theme color path (e.g., 'primary.main') or a CSS color value.
   * @default 'transparent'
   */
  backgroundColor?: string;
  /**
   * The color of the selection indicator.
   *
   * - `variant="icon"`: color of the 72×48 oblong shape behind the icon.
   * - `variant="pill"`: fill color of the pill when `selected` is true.
   * @default 'secondary.main'
   */
  selectionColor?: string;
  /**
   * Callback fired when the button is clicked.
   */
  onClick?: MouseEventHandler<HTMLDivElement | HTMLAnchorElement>;
  /**
   * If `true`, the button will be disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * The data-testid attribute for testing.
   */
  'data-testid'?: string;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps<Theme>;
  /**
   * Render as a different element. Pair with `href` (or `to` when
   * `component` is react-router's `Link`) to render a real navigable element
   * instead of the default `role="button"` div. Native anchors give
   * middle-click / cmd-click / right-click "open in new tab" for free and
   * surface the URL in the browser status bar.
   */
  component?: ElementType;
  /**
   * Native anchor target — used when rendering as a plain `<a>` (or any
   * element that accepts `href`).
   */
  href?: string;
  /**
   * Pass-through for routing libraries that use a `to` prop (e.g.
   * react-router's `Link`). ui-library doesn't depend on react-router; this
   * is forwarded as an opaque value to whatever `component` resolves to.
   */
  to?: string;
};

/**
 * NavigationButton props require either a label or aria-label for accessibility.
 * - label: string | ReactNode. When string, rendered in Typography; when ReactNode (e.g. text + badge), rendered as-is.
 * - When label is custom (ReactNode), aria-label should be set for accessibility.
 */
export type NavigationButtonProps = BaseNavigationButtonProps &
  (
    | {
        /**
         * Label below the icon (icon variant) or inline next to the icon (pill
         * variant): string (rendered in Typography) or ReactNode.
         * When ReactNode, pass aria-label for accessibility.
         */
        label: string | React.ReactNode;
        /**
         * Optional. Defaults to label when label is a string. Set when label is custom (ReactNode).
         */
        'aria-label'?: string;
      }
    | {
        label?: never;
        'aria-label': string;
      }
  );

/**
 * NavigationButton Component
 *
 * Shared primitive for navigation links/buttons across mobile and desktop.
 * Pick a visual shape with `variant`:
 *
 * - **`variant="icon"`** (default) — mobile bottom-nav: icon on top, label
 *   below, 72×48 oblong selection indicator.
 * - **`variant="pill"`** — desktop top-nav / horizontal tabs: optional icon +
 *   label inline, the whole button becomes a pill that fills with
 *   `selectionColor` when selected. Combine with `component={RouterLink}` +
 *   `to="/path"` to render as a real anchor for native-link semantics.
 *
 * The shared color tokens (`iconColor`, `textColor`, `backgroundColor`,
 * `selectionColor`) work the same in both variants.
 *
 * @example
 * // Mobile bottom-nav (default variant):
 * <NavigationButton
 *   icon={<HomeIcon />}
 *   label="Home"
 *   selected={isHomeSelected}
 *   onClick={() => navigate('/home')}
 *   aria-label="Navigate to home"
 * />
 *
 * @example
 * // Desktop top-nav, rendered as a real react-router Link:
 * <NavigationButton
 *   variant="pill"
 *   component={RouterLink}
 *   to="/lidar"
 *   label="LiDAR"
 *   selected={pathname.startsWith('/lidar')}
 *   selectionColor="primary.main"
 *   textColor="common.white"
 * />
 */
export const NavigationButton = forwardRef<HTMLDivElement, NavigationButtonProps>(
  (
    {
      icon,
      label,
      variant = 'icon',
      selected = false,
      iconColor = 'common.white',
      textColor = 'common.white',
      backgroundColor = 'transparent',
      selectionColor = 'secondary.main',
      onClick,
      disabled = false,
      'aria-label': ariaLabel,
      'data-testid': dataTestId,
      sx,
      component,
      href,
      to,
    },
    ref
  ) => {
    const isPill = variant === 'pill';
    const isAnchor = component !== undefined || href !== undefined || to !== undefined;
    // Anchors are natively focusable and keyboard-activated by the browser,
    // and forwarding `role="button"` would override their semantics. Only
    // emulate button semantics on the default <Box> path.
    const buttonSemantics = isAnchor
      ? null
      : {
          role: 'button' as const,
          tabIndex: disabled ? -1 : 0,
          onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ') && onClick) {
              e.preventDefault();
              e.currentTarget.click();
            }
          },
        };

    // Disabled anchors must call preventDefault on click — `onClick={undefined}`
    // alone doesn't stop native href/to navigation or react-router `<Link>`
    // (Link's internal handler bails when `event.defaultPrevented` is true).
    // Combined with `tabIndex={-1}` on the anchor render paths below, this
    // makes a disabled anchor non-interactive end-to-end.
    const handleClick: MouseEventHandler<HTMLDivElement | HTMLAnchorElement> = (e) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      onClick?.(e);
    };

    const containerStyles: SxProps<Theme> = isPill
      ? {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: icon ? 1 : 0,
          height: 40,
          px: 2.5,
          borderRadius: '999px',
          color: textColor,
          backgroundColor: selected ? selectionColor : backgroundColor,
          textDecoration: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          userSelect: 'none',
          transition: 'background-color 120ms ease, color 120ms ease',
          '&:hover': {
            backgroundColor: selected ? selectionColor : 'rgba(255, 255, 255, 0.08)',
          },
        }
      : {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 0.5,
          minWidth: 72,
          padding: '4px 8px',
          backgroundColor,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          userSelect: 'none',
        };

    const selectionShapeContainerStyles: SxProps<Theme> = {
      position: 'relative',
      width: 72,
      height: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    const selectionIndicatorStyles: SxProps<Theme> = {
      position: 'absolute',
      top: 0,
      left: 0,
      width: 72,
      height: 48,
      borderRadius: '24px',
      backgroundColor: selectionColor,
      zIndex: 0,
      display: selected ? 'block' : 'none',
    };

    const iconContainerStyles: SxProps<Theme> = {
      position: 'relative',
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: iconColor,
      '& .MuiSvgIcon-root': {
        fontSize: '2rem !important',
      },
      '& svg': {
        fontSize: '2rem !important',
        width: '2rem',
        height: '2rem',
      },
    };

    const renderLabel = (textVariant: 'button' | 'pill') => {
      if (!label) return null;
      if (typeof label === 'string') {
        // Both variants use Typography's `button` variant — same weight (500),
        // letter-spacing, and font size as the mobile NavigationButton, so
        // the type weight feels consistent across the design system.
        // For pills we drop uppercase so labels like "LiDAR" keep their casing
        // (mobile labels like "Home" / "Map" stay uppercase as before).
        const isPillLabel = textVariant === 'pill';
        return (
          <Typography
            variant="button"
            color={isPillLabel ? undefined : textColor}
            sx={
              isPillLabel
                ? {
                    color: 'inherit',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                  }
                : undefined
            }
          >
            {label}
          </Typography>
        );
      }
      return label;
    };

    if (isPill) {
      return (
        <Box
          ref={ref}
          component={component ?? 'div'}
          {...(href !== undefined ? { href } : {})}
          {...(to !== undefined ? { to } : {})}
          {...buttonSemantics}
          {...(isAnchor && disabled ? { tabIndex: -1 } : {})}
          aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
          aria-disabled={disabled}
          aria-current={isAnchor && selected ? 'page' : undefined}
          data-testid={dataTestId}
          sx={sx ? [containerStyles, ...(Array.isArray(sx) ? sx : [sx])] : containerStyles}
          onClick={handleClick}
        >
          {icon && (
            <Box
              data-testid="icon-container"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                color: iconColor,
                '& svg': { fontSize: '1.25rem' },
              }}
            >
              {icon}
            </Box>
          )}
          {renderLabel('pill')}
        </Box>
      );
    }

    return (
      <Box
        ref={ref}
        component={component ?? 'div'}
        {...(href !== undefined ? { href } : {})}
        {...(to !== undefined ? { to } : {})}
        {...buttonSemantics}
        {...(isAnchor && disabled ? { tabIndex: -1 } : {})}
        aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
        aria-disabled={disabled}
        data-testid={dataTestId}
        aria-current={isAnchor && selected ? 'page' : undefined}
        sx={sx ? [containerStyles, ...(Array.isArray(sx) ? sx : [sx])] : containerStyles}
        onClick={handleClick}
      >
        <Box sx={selectionShapeContainerStyles}>
          <Box sx={selectionIndicatorStyles} data-testid="selection-indicator" />
          <Box sx={iconContainerStyles} data-testid="icon-container">
            {icon}
          </Box>
        </Box>
        {renderLabel('button')}
      </Box>
    );
  }
);

NavigationButton.displayName = 'NavigationButton';
